import {
  createAlignPlugin,
  createBoldPlugin,
  createItalicPlugin,
  createParagraphPlugin,
  createLinkPlugin,
  createExitBreakPlugin,
  createUnderlinePlugin,
  createNodeIdPlugin,
  createPlugins,
  createResetNodePlugin,
  createTrailingBlockPlugin,
  createPlateUI,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_PARAGRAPH,
  ELEMENT_LINK,
  ELEMENT_UL,
  ELEMENT_OL,
  ELEMENT_LI,
  ELEMENT_TODO_LI,
  ELEMENT_MEDIA_EMBED,
  ELEMENT_TABLE,
  ELEMENT_TD,
  ELEMENT_TH,
  ELEMENT_IMAGE,
  StyledElement,
} from '@udecode/plate';

import { createErrorHandlingPlugin } from '../plugins/error/createErrorHandlingPlugin';
import { createListPlugin } from '../plugins/list/createListPlugin';
import { listPlugin, listPluginByKey } from '../plugins/list/listPlugin';
import { linkPlugin } from '../plugins/link/linkPlugin';
import { PlateFloatingLink } from '../components/popup/FloatingLink/PlateFloatingLink';
import { VALID_ALIGNMENT_TYPES } from '../plugins/alignment/constants';
import { createFakeSelectionPlugin } from '../plugins/fake-selection/createFakeSelectionPlugin';
import { resetNodePlugin } from '../plugins/reset-node/resetNodePlugin';
import {
  createMergeFieldsPlugin,
  ELEMENT_MERGE_FIELD,
  ELEMENT_SMART_INPUT,
} from '../plugins/merge-fields/createMergeFieldsPlugin';
import { TodoListElement } from '../plugins/todo-list/ToDoListElement';
import { PlateTableElement } from '../plugins/table/PlateTableElement';
import { PlateTableCellElement } from '../plugins/table/PlateTableCellElement';
import { ELEMENT_PAGE_BREAK } from '../plugins/page-break/createPageBreakPlugin';
import { PageBreakElement } from '../plugins/page-break/PageBreakElement';
import { ImageElement } from '../plugins/media/images/ImageElement';
import { MediaEmbedElement } from '../plugins/media/embed/MediaEmbedElement';
import { ListItemElement } from '../plugins/list/ListItemElement';
import { LinkElement } from '../plugins/link/LinkElement';
import { NOTE_HEADER, NOTE_HEADER_VOID } from '../plugins/layout/note-layout/createNoteLayoutPlugin';
import { NoteHeaderElement } from '../plugins/layout/note-layout/NoteHeaderElement';
import { MergeFieldElement } from '../plugins/merge-fields/MergeFieldElement';
import { SmartInputElement } from '../plugins/merge-fields/SmartInputElement';

const baseTextStyle = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  lineHeight: 1.2,
  margin: 0,
};

// Custom withProps function so that the plate ui can combine inline styles,
// rather than overwrite.
const withProps = (Component, props) => {
  return (_props) => {
    const { style: style1 = {}, ...rest1 } = _props;
    const { style: style2 = {}, ...rest2 } = props;
    return (
      <Component
        {...rest1}
        {...rest2}
        style={{ ...style1, ...style2 }}
      />
    );
  };
};

export const plateUI = createPlateUI({
  [ELEMENT_H1]: withProps(StyledElement, { as: 'h1', style: baseTextStyle }),
  [ELEMENT_H2]: withProps(StyledElement, { as: 'h2', style: baseTextStyle }),
  [ELEMENT_H3]: withProps(StyledElement, { as: 'h3', style: baseTextStyle }),
  [ELEMENT_H4]: withProps(StyledElement, { as: 'h4', style: baseTextStyle }),
  [ELEMENT_H5]: withProps(StyledElement, { as: 'h5', style: baseTextStyle }),
  [ELEMENT_H6]: withProps(StyledElement, { as: 'h6', style: baseTextStyle }),
  [ELEMENT_PARAGRAPH]: withProps(
    StyledElement,
    {
      as: 'p',
      style: { ...baseTextStyle, fontSize: '13px', minHeight: '13px' },
    },
  ),
  [ELEMENT_LINK]: withProps(LinkElement, { style: { color: '#bea06e', textDecoration: 'underline' } }),
  [ELEMENT_OL]: withProps(StyledElement, { as: 'ol', style: { paddingLeft: '10px', margin: '0' } }),
  [ELEMENT_UL]: withProps(StyledElement, { as: 'ul', style: { paddingLeft: '10px', margin: '0' } }),
  [ELEMENT_LI]: ListItemElement,
  [ELEMENT_TODO_LI]: TodoListElement,
  [ELEMENT_IMAGE]: ImageElement,
  [ELEMENT_MEDIA_EMBED]: withProps(MediaEmbedElement, {
    caption: { disabled: true },
  }),
  [ELEMENT_TABLE]: PlateTableElement,
  [ELEMENT_TD]: PlateTableCellElement,
  [ELEMENT_TH]: PlateTableCellElement,
  [ELEMENT_PAGE_BREAK]: PageBreakElement,
  [NOTE_HEADER]: NoteHeaderElement,
  [NOTE_HEADER_VOID]: NoteHeaderElement,
  [ELEMENT_MERGE_FIELD]: MergeFieldElement,
  [ELEMENT_SMART_INPUT]: SmartInputElement,
});

const components = plateUI;

export const plugins = createPlugins(
  [
    createTrailingBlockPlugin({ level: 0, type: ELEMENT_PARAGRAPH }),
    createErrorHandlingPlugin(),
    createResetNodePlugin(resetNodePlugin),
    createParagraphPlugin(),
    createAlignPlugin({
      inject: {
        props: {
          validTypes: VALID_ALIGNMENT_TYPES,
        },
      },
    }),
    createListPlugin(
      listPlugin,
      {
        ...listPluginByKey,
        [ELEMENT_OL]: {
          ...listPluginByKey[ELEMENT_OL],
          serializeHtml: withProps(StyledElement, { as: 'ol', style: { paddingLeft: '20px', margin: '0' } }),
        },
        [ELEMENT_UL]: {
          ...listPluginByKey[ELEMENT_UL],
          serializeHtml: withProps(StyledElement, { as: 'ul', style: { paddingLeft: '20px', margin: '0' } }),
        },
        [ELEMENT_LI]: {
          ...listPluginByKey[ELEMENT_LI],
          serializeHtml: withProps(ListItemElement, { style: baseTextStyle }),
        },
      },
    ),
    createLinkPlugin({
      ...linkPlugin,
      serializeHtml: withProps(LinkElement, { style: { color: '#bea06e', textDecoration: 'underline' } }),
      renderAfterEditable: PlateFloatingLink,
    }),

    createBoldPlugin(),
    createItalicPlugin(),
    createUnderlinePlugin(),

    createMergeFieldsPlugin(),

    createExitBreakPlugin(),

    createNodeIdPlugin(),
    createFakeSelectionPlugin(),
  ],
  { components },
);

export const serializedPlugins = [
  createParagraphPlugin(),
  createAlignPlugin({
    inject: {
      props: {
        validTypes: VALID_ALIGNMENT_TYPES,
      },
    },
  }),
  createListPlugin(listPlugin, listPluginByKey),
  createLinkPlugin({ ...linkPlugin, renderAfterEditable: PlateFloatingLink }),

  createBoldPlugin(),
  createItalicPlugin(),
  createUnderlinePlugin(),

  createExitBreakPlugin(),

  { components: plateUI },
];
